import { FilterStatsDto } from '@/app/common/general-types/filters.dto';
import {
  AddBriefDemographicsDto,
  BriefResponseDto,
  DeliveryTypeEnum,
  Piece,
  ScreeningExpectedAnswer,
  ScreeningQuestionTypeEnum,
  SocialChannelEnum,
  VideoFormatEnum,
} from '@/app/common/general-types/briefs.dto';
import { InfluencerLevel, NewBriefPricingDto } from '@/app/common/general-types/pricing.dto';
import { pieceRateUsage } from '@/app/common/brief-pieces/piece-rate-usage';
import { PLACEMENT_CHANNEL_LABEL } from '@/app/common/brief-pieces/piece-rules';

/**
 * Pure logic for the Briefing tab: what each section shows, whether it counts as set, and the
 * shapes the drawers edit. Kept free of Angular so it is tested with plain Vitest.
 */

export type BriefingSectionKey =
  | 'basic'
  | 'gift'
  | 'dist'
  | 'docs'
  | 'legal'
  | 'crit'
  | 'scr'
  | 'comp'
  | 'hire';

/** Set, Not set, or (Creative Direction only) Needs more detail. */
export type SectionStatus = 'set' | 'missing' | 'weak';

export type BriefKind = 'ugc' | 'influence';

// ---------------------------------------------------------------------------
// Brief type and counts
// ---------------------------------------------------------------------------

/** Influence when any video is posted to a social channel; UGC Only when videos just come to the brand. */
export function briefKind(brief: Pick<BriefResponseDto, 'pieces' | 'shoutBrief'>): BriefKind {
  if (brief.pieces?.length) {
    return brief.pieces.some((piece) => piece.placements.some((p) => p.kind === 'social')) ? 'influence' : 'ugc';
  }
  const platform = brief.shoutBrief?.socialPlatform;
  return brief.shoutBrief && platform != null && Number(platform) !== SocialChannelEnum['Not Set'] ? 'influence' : 'ugc';
}

/** Creators at or past Hired. */
export function hiredCount(stats: FilterStatsDto): number {
  return stats.Hired + stats.Started + stats.ReadyToPost + stats.Posted + stats.Paid + stats.Completed;
}

/** Creators who applied (including everyone since hired). Invited-only creators haven't answered screening. */
export function appliedCount(stats: FilterStatsDto): number {
  return stats.Applied + hiredCount(stats);
}

export function plural(count: number, one: string, many = `${one}s`): string {
  return `${count} ${count === 1 ? one : many}`;
}

// ---------------------------------------------------------------------------
// Rich text <-> lists (do's and don'ts)
// ---------------------------------------------------------------------------

const ENTITIES: Record<string, string> = { amp: '&', lt: '<', gt: '>', quot: '"', '#39': "'", apos: "'", nbsp: ' ' };

function decodeEntities(text: string): string {
  return text.replace(/&(amp|lt|gt|quot|#39|apos|nbsp);/g, (_, name: string) => ENTITIES[name]);
}

function stripTags(html: string): string {
  return decodeEntities(html.replace(/<[^>]*>/g, '')).replace(/\s+/g, ' ').trim();
}

/**
 * The items of a do's / don'ts field. List items when it has a list, otherwise one item per
 * paragraph or line. Formatting inside an item is dropped.
 */
export function htmlToItems(html: string | null | undefined): string[] {
  if (!html?.trim()) {
    return [];
  }
  const listItems = [...html.matchAll(/<li\b[^>]*>([\s\S]*?)<\/li>/gi)].map((match) => stripTags(match[1]));
  const parts = listItems.length
    ? listItems
    : html
        .split(/<\/p>|<br\s*\/?>|<\/div>|\n/i)
        .map((part) => stripTags(part).replace(/^[-•*]\s*/, ''));
  return parts.filter((item) => item.length > 0);
}

function escapeHtml(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/** Saves a list back as the HTML the API, Noodle and TikTok One read. Empty items are dropped. */
export function itemsToHtml(items: readonly string[]): string {
  const kept = items.map((item) => item.trim()).filter(Boolean);
  return kept.length ? `<ul>${kept.map((item) => `<li>${escapeHtml(item)}</li>`).join('')}</ul>` : '';
}

/** Plain text of the key message (the brief overview, stored as rich text). */
export function plainText(html: string | null | undefined): string {
  return html ? stripTags(html.replace(/<\/(p|li|div)>|<br\s*\/?>/gi, ' ')) : '';
}

// ---------------------------------------------------------------------------
// Creative Direction checks: the API's content-review bar
// ---------------------------------------------------------------------------

export type QualityField = 'key' | 'dos' | 'donts';
export type QualityIssue = { field: QualityField; title: string; detail: string };

/**
 * The plain-text lengths the API needs before it sends a brief for content review
 * (`checkEligibilityForContentReview`): more than these, or an uploaded brief document.
 * The same rule the old Documentation section flagged; individual items are not judged.
 */
export const CONTENT_REVIEW_MIN = { key: 150, dos: 25, donts: 25 } as const;

export type CreativeText = { key: string; dos: string; donts: string; hasDocument: boolean };

export function qualityIssues(text: CreativeText): QualityIssue[] {
  if (text.hasDocument) {
    return [];
  }
  const issues: QualityIssue[] = [];
  if (text.key.trim().length <= CONTENT_REVIEW_MIN.key) {
    issues.push({
      field: 'key',
      title: text.key.trim() ? 'Key message needs more detail' : 'Add a key message',
      detail: `Write more than ${CONTENT_REVIEW_MIN.key} characters: background, facts and the main points every video should cover.`,
    });
  }
  if (text.dos.trim().length <= CONTENT_REVIEW_MIN.dos) {
    issues.push({
      field: 'dos',
      title: text.dos.trim() ? 'Do’s need more detail' : 'Add your do’s',
      detail: 'List what must be in every video, like product features, talking points or brand visibility.',
    });
  }
  if (text.donts.trim().length <= CONTENT_REVIEW_MIN.donts) {
    issues.push({
      field: 'donts',
      title: text.donts.trim() ? 'Don’ts need more detail' : 'Add your don’ts',
      detail: 'List what videos must avoid, like competitor mentions or bad language, so they don’t get sent back.',
    });
  }
  return issues;
}

export const QUALITY_CHECKS: readonly { field: QualityField; label: string }[] = [
  { field: 'key', label: 'Key message has enough detail' },
  { field: 'dos', label: 'Do’s have enough detail' },
  { field: 'donts', label: 'Don’ts have enough detail' },
];

/** The saved brief's text as the API measures it. */
export function creativeText(
  brief: Pick<BriefResponseDto, 'stepsHtml' | 'stepsPlainText' | 'doHtml' | 'doPlainText' | 'dontHtml' | 'dontPlainText' | 'briefDocumentUrl'>,
): CreativeText {
  return {
    key: brief.stepsPlainText ?? plainText(brief.stepsHtml),
    dos: brief.doPlainText ?? plainText(brief.doHtml),
    donts: brief.dontPlainText ?? plainText(brief.dontHtml),
    hasDocument: !!brief.briefDocumentUrl?.trim(),
  };
}

// ---------------------------------------------------------------------------
// Section status (handover §3 "Set rules", update 1 §1)
// ---------------------------------------------------------------------------

export function sectionStatus(key: BriefingSectionKey, brief: BriefResponseDto): SectionStatus {
  switch (key) {
    case 'basic':
      return brief.title?.trim() && brief.productId ? 'set' : 'missing';
    case 'gift':
      // Every brief has a delivery method; "none" is a choice.
      return brief.deliveryType != null ? 'set' : 'missing';
    case 'dist':
      return videoCount(brief) > 0 ? 'set' : 'missing';
    case 'docs':
      return creativeStatus(brief);
    case 'legal':
      return brief.complianceDocIds?.length ? 'set' : 'missing';
    case 'crit':
      return hasCriteria(brief.demographics) || hasPersona(brief.creatorPersona) ? 'set' : 'missing';
    case 'scr':
      return brief.screeningQuestions?.length ? 'set' : 'missing';
    case 'comp':
      return payView(brief).baseMin > 0 || brief.brief_remuneration_type != null || !!brief.enableAffiliateMarketing
        ? 'set'
        : 'missing';
    case 'hire':
      return brief.hireMessage?.trim() ? 'set' : 'missing';
  }
}

/**
 * Set needs a key message and a do, or an uploaded brief document (which stands in for both).
 * Needs more detail only when the API says the brief isn't ready for content review: it also
 * accepts some shorter briefs after an AI check, so it is the source of truth, not the lengths.
 */
function creativeStatus(
  brief: Pick<BriefResponseDto, 'stepsHtml' | 'doHtml' | 'briefDocumentUrl' | 'isReadyForContentReview'>,
): SectionStatus {
  const hasDocument = !!brief.briefDocumentUrl?.trim();
  if (!hasDocument && (!plainText(brief.stepsHtml) || htmlToItems(brief.doHtml).length === 0)) {
    return 'missing';
  }
  return brief.isReadyForContentReview === false ? 'weak' : 'set';
}

// ---------------------------------------------------------------------------
// Deliverables
// ---------------------------------------------------------------------------

export type Orientation = 'vertical' | 'horizontal' | 'square';

export const ORIENTATION_LABEL: Record<Orientation, { ratio: string; name: string; use: string }> = {
  vertical: { ratio: '9:16', name: 'Vertical', use: 'TikTok, Reels, Shorts' },
  square: { ratio: '1:1', name: 'Square', use: 'Feed posts' },
  horizontal: { ratio: '16:9', name: 'Landscape', use: 'YouTube, website' },
};

export const DURATION_LABEL: Record<15 | 30 | 60, string> = {
  15: 'Up to 15 sec',
  30: '15–30 sec',
  60: '30–60 sec',
};

export type DeliverableVideo = {
  label: string;
  orientation: Orientation;
  duration: 15 | 30 | 60;
  /** Social channels this video is posted to; empty for a UGC-only video. */
  channels: string[];
};

const LEGACY_CHANNEL_LABEL: Record<number, string> = {
  [SocialChannelEnum['TikTok Post']]: 'TikTok Post',
  [SocialChannelEnum['Instagram Reel']]: 'Instagram Reel',
  [SocialChannelEnum['Instagram Story']]: 'Instagram Story',
  [SocialChannelEnum['YouTube Shorts']]: 'YouTube Shorts',
  [SocialChannelEnum['Snapchat Spotlight']]: 'Snapchat Spotlight',
  [SocialChannelEnum['Snapchat Story']]: 'Snapchat Story',
};

const FORMAT_ORIENTATION: Record<VideoFormatEnum, Orientation> = {
  [VideoFormatEnum.Vertical]: 'vertical',
  [VideoFormatEnum.Horizontal]: 'horizontal',
  [VideoFormatEnum.Square]: 'square',
};

/** Legacy briefs store the length as whichever 15 / 30 / 60 sec rate is set. */
export function legacyDuration(price: BriefPriceView | null | undefined): 15 | 30 | 60 {
  if ((price?.fifteenSecPrice ?? 0) > 0) return 15;
  if ((price?.thirtySecPrice ?? 0) > 0) return 30;
  if ((price?.sixtySecPrice ?? 0) > 0) return 60;
  return 30;
}

export function videoCount(brief: Pick<BriefResponseDto, 'pieces' | 'videosPerCreator'>): number {
  return brief.pieces?.length ?? brief.videosPerCreator ?? 0;
}

/** One entry per video each creator makes. Legacy briefs have no names, so videos are numbered. */
export function deliverableVideos(brief: BriefResponseDto): DeliverableVideo[] {
  if (brief.pieces?.length) {
    return brief.pieces.map((piece: Piece) => ({
      label: piece.label,
      orientation: piece.format,
      duration: piece.durationSeconds,
      channels: piece.placements.flatMap((p) => (p.kind === 'social' ? [PLACEMENT_CHANNEL_LABEL[p.channel]] : [])),
    }));
  }
  const orientation = FORMAT_ORIENTATION[brief.videoFormat] ?? 'vertical';
  const duration = legacyDuration(brief.currentPrice);
  const channels = briefKind(brief) === 'influence' ? legacyChannels(brief) : [];
  return Array.from({ length: brief.videosPerCreator ?? 0 }, (_, i) => ({
    label: `Video ${i + 1}`,
    orientation,
    duration,
    channels,
  }));
}

function legacyChannels(brief: BriefResponseDto): string[] {
  const shout = brief.shoutBrief;
  if (!shout) {
    return [];
  }
  const ids = [shout.socialPlatform, ...(shout.crossPlatforms ?? [])].map(Number);
  return [...new Set(ids.map((id) => LEGACY_CHANNEL_LABEL[id]).filter(Boolean))];
}

/** "Vertical 9:16" when every video shares an orientation, otherwise null (shown per video). */
export function sharedValue<T>(values: readonly T[]): T | null {
  return values.length && values.every((v) => v === values[0]) ? values[0] : null;
}

// ---------------------------------------------------------------------------
// Pay
// ---------------------------------------------------------------------------

/** The parts of `currentPrice` the tab reads (amounts in pence). */
export type BriefPriceView = {
  facePrice?: number;
  ugcPrice?: number;
  advertisingPrice?: number;
  socialPostPrice?: number;
  fifteenSecPrice?: number;
  thirtySecPrice?: number;
  sixtySecPrice?: number;
  adLicenseSelected?: boolean | null;
  currencyCode?: string;
  influencerLevels?: InfluencerLevel[];
};

export type PayTier = { title: string; followers: string; bars: number; bonus: number; earns: number };

export type PayView = {
  /** Lowest and highest per-video price in major units; equal unless a piece brief prices videos differently. */
  baseMin: number;
  baseMax: number;
  videos: number;
  /** Each creator's base earnings (no audience bonus), in major units. */
  total: number;
  /** Per-video prices, for the breakdown under a range. Empty on a legacy brief. */
  perVideo: { label: string; amount: number }[];
  tiers: PayTier[];
  currency: string;
};

function legacyBase(price: BriefPriceView): number {
  return (
    (price.ugcPrice ?? 0) +
    (price.facePrice ?? 0) +
    (price.fifteenSecPrice ?? 0) +
    (price.thirtySecPrice ?? 0) +
    (price.sixtySecPrice ?? 0) +
    (price.socialPostPrice ?? 0) +
    (price.advertisingPrice ?? 0)
  );
}

export function formatFollowers(value: number): string {
  if (value >= 1_000_000) {
    const m = value / 1_000_000;
    return `${Number.isInteger(m) ? m : m.toFixed(1)}M`;
  }
  if (value >= 1000) {
    const k = value / 1000;
    return `${Number.isInteger(k) ? k : k.toFixed(1)}K`;
  }
  return String(value);
}

export function payView(brief: BriefResponseDto): PayView {
  const price: BriefPriceView | null = brief.currentPrice ?? null;
  const currency = (price?.currencyCode ?? 'GBP').toUpperCase();
  if (!price) {
    return { baseMin: 0, baseMax: 0, videos: videoCount(brief), total: 0, perVideo: [], tiers: [], currency };
  }
  let perVideo: { label: string; amount: number }[] = [];
  let baseMin: number;
  let baseMax: number;
  let total: number;
  const videos = videoCount(brief);
  if (brief.pieces?.length) {
    const amounts = pieceRateUsage(brief.pieces, price).perPiece.map((pence) => pence / 100);
    perVideo = brief.pieces.map((piece, i) => ({ label: piece.label, amount: amounts[i] }));
    baseMin = Math.min(...amounts);
    baseMax = Math.max(...amounts);
    total = amounts.reduce((sum, a) => sum + a, 0);
  } else {
    baseMin = baseMax = legacyBase(price) / 100;
    total = baseMin * videos;
  }
  const levels = [...(price.influencerLevels ?? [])].sort((a, b) => a.followersFrom - b.followersFrom);
  const tiers = briefKind(brief) === 'influence'
    ? levels.map((level, i) => ({
        title: level.title,
        followers:
          i === levels.length - 1 || level.followersTo >= 100_000_000
            ? `${formatFollowers(level.followersFrom)}+`
            : `${formatFollowers(level.followersFrom)}–${formatFollowers(level.followersTo)}`,
        bars: Math.min(4, Math.max(1, Math.round(((i + 1) / levels.length) * 4))),
        bonus: level.bonusValue / 100,
        earns: total + (level.bonusValue / 100) * videos,
      }))
    : [];
  return { baseMin, baseMax, videos, total, perVideo, tiers, currency };
}

// ---------------------------------------------------------------------------
// Product delivery
// ---------------------------------------------------------------------------

export const DELIVERY_OPTIONS: readonly {
  type: DeliveryTypeEnum;
  label: string;
  icon: 'circle-slash' | 'ticket-percent' | 'package';
  description: string;
}[] = [
  {
    type: DeliveryTypeEnum.none,
    label: 'No delivery',
    icon: 'circle-slash',
    description: 'Creators already have the product, or don’t need it.',
  },
  {
    type: DeliveryTypeEnum.Discount_Code,
    label: 'Coupon code',
    icon: 'ticket-percent',
    description: 'You share a coupon code so each creator can buy the product.',
  },
  {
    type: DeliveryTypeEnum.Address_Required,
    label: 'Ship the product',
    icon: 'package',
    description: 'You ship the product to each creator’s address.',
  },
];

// ---------------------------------------------------------------------------
// Who Can Apply
// ---------------------------------------------------------------------------

export type PersonaField =
  | 'creatorPersona'
  | 'creatorAudience'
  | 'creatorContentStyle'
  | 'creatorEngagementMetrics'
  | 'creatorSpecificCharacteristics';

export const PERSONA_FIELDS: readonly { key: PersonaField; label: string }[] = [
  { key: 'creatorPersona', label: 'Creator persona' },
  { key: 'creatorAudience', label: 'Their audience' },
  { key: 'creatorContentStyle', label: 'Content style' },
  { key: 'creatorEngagementMetrics', label: 'Engagement' },
  { key: 'creatorSpecificCharacteristics', label: 'Must have' },
];

export type PersonaDraft = Record<PersonaField, string>;

export function personaDraft(persona: Partial<Record<PersonaField, string | null>> | null | undefined): PersonaDraft {
  return {
    creatorPersona: persona?.creatorPersona ?? '',
    creatorAudience: persona?.creatorAudience ?? '',
    creatorContentStyle: persona?.creatorContentStyle ?? '',
    creatorEngagementMetrics: persona?.creatorEngagementMetrics ?? '',
    creatorSpecificCharacteristics: persona?.creatorSpecificCharacteristics ?? '',
  };
}

export function hasPersona(persona: Partial<Record<PersonaField, string | null>> | null | undefined): boolean {
  return PERSONA_FIELDS.some(({ key }) => !!persona?.[key]?.trim());
}

/** Gender 0 means Any; an empty list or just [0] is no rule. */
export function genderIds(demographics: Pick<AddBriefDemographicsDto, 'gender'> | null | undefined): number[] {
  return (demographics?.gender ?? []).map(Number).filter((id) => id !== 0);
}

export function hasCriteria(demographics: AddBriefDemographicsDto | null | undefined): boolean {
  if (!demographics) {
    return false;
  }
  return (
    !!demographics.interests?.length ||
    !!demographics.age?.length ||
    !!demographics.countryCodes?.length ||
    !!demographics.location?.length ||
    !!demographics.followerRanges?.length ||
    !!demographics.is18plus ||
    genderIds(demographics).length > 0
  );
}

/** "18–34" from the brackets 18–21 + 21–25 + 25–30 + 30–35, or a list when they don't join up. */
export function ageSummary(age: readonly { minAge: number; maxAge: number; label: string }[]): string | null {
  if (!age.length) {
    return null;
  }
  const sorted = [...age].sort((a, b) => a.minAge - b.minAge);
  const joined = sorted.every((bracket, i) => i === 0 || bracket.minAge === sorted[i - 1].maxAge);
  return joined
    ? `${sorted[0].minAge}–${sorted[sorted.length - 1].maxAge}`
    : sorted.map((bracket) => bracket.label.replace(/\s*-\s*/, '–')).join(', ');
}

/** "1K+ followers" for one band starting at 1K, otherwise the band names. */
export function audienceSummary(ranges: readonly { min: number; max: number }[], labels: ReadonlyMap<string, string>): string | null {
  if (!ranges.length) {
    return null;
  }
  return [...ranges]
    .sort((a, b) => a.min - b.min)
    .map((range) => labels.get(`${range.min}-${range.max}`) ?? `${formatFollowers(range.min)}–${formatFollowers(range.max)}`)
    .join(', ');
}

// ---------------------------------------------------------------------------
// Screening questions
// ---------------------------------------------------------------------------

export const MAX_SCREENING_QUESTIONS = 3;

export type ScreeningDraft = {
  id?: string;
  question: string;
  type: ScreeningQuestionTypeEnum;
  /** Yes/No only. */
  expectedYesNo: 'Yes' | 'No' | '';
  /** Number only. */
  op: 'gt' | 'lt' | 'eq';
  value: string;
};

export const SCREENING_TYPE_LABEL: Record<ScreeningQuestionTypeEnum, string> = {
  [ScreeningQuestionTypeEnum.OneLine]: 'Short text',
  [ScreeningQuestionTypeEnum.YesNo]: 'Yes / No',
  [ScreeningQuestionTypeEnum.Numerical]: 'Number',
};

export const OPERATOR_WORDS: Record<'gt' | 'lt' | 'eq', string> = { gt: 'more than', lt: 'less than', eq: 'exactly' };
export const OPERATOR_SYMBOL: Record<'gt' | 'lt' | 'eq', string> = { gt: '>', lt: '<', eq: '=' };

export type StoredScreeningQuestion = {
  id?: string;
  question: string;
  type: ScreeningQuestionTypeEnum;
  expected?: ScreeningExpectedAnswer;
};

export function newScreeningDraft(): ScreeningDraft {
  return { question: '', type: ScreeningQuestionTypeEnum.YesNo, expectedYesNo: '', op: 'gt', value: '' };
}

export function screeningDraft(question: StoredScreeningQuestion): ScreeningDraft {
  const expected = question.expected;
  return {
    id: question.id,
    question: question.question,
    type: question.type,
    expectedYesNo: expected?.kind === 'exact' ? expected.value : '',
    op: expected?.kind === 'number' ? expected.operator : 'gt',
    value: expected?.kind === 'number' ? String(expected.value) : '',
  };
}

export function screeningPayload(draft: ScreeningDraft): StoredScreeningQuestion {
  const base = { ...(draft.id ? { id: draft.id } : {}), question: draft.question.trim(), type: draft.type };
  if (draft.type === ScreeningQuestionTypeEnum.YesNo && draft.expectedYesNo) {
    return { ...base, expected: { kind: 'exact', value: draft.expectedYesNo } };
  }
  const value = draft.value.trim() === '' ? NaN : Number(draft.value);
  if (draft.type === ScreeningQuestionTypeEnum.Numerical && Number.isFinite(value)) {
    return { ...base, expected: { kind: 'number', operator: draft.op, value } };
  }
  return base;
}

/** The pill text under a question, or null when it's reviewed manually. */
export function expectedSummary(question: StoredScreeningQuestion): string | null {
  const expected = question.expected;
  if (expected?.kind === 'exact') {
    return `Auto-qualifies creators who answer ${expected.value}`;
  }
  if (expected?.kind === 'number') {
    return `Auto-qualifies creators who answer ${OPERATOR_WORDS[expected.operator]} ${expected.value}`;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Legacy video length (moved from the old documentation editor)
// ---------------------------------------------------------------------------

/**
 * Legacy briefs store the length as mutually exclusive 15 / 30 / 60 sec prices on the current
 * price. Copy the rest of the price and put the model's (or current) amount in the chosen bucket.
 */
export function pricingForDuration(brief: BriefResponseDto, seconds: 15 | 30 | 60): NewBriefPricingDto | undefined {
  const current = (brief.currentPrice ?? brief.briefPricing) as Partial<NewBriefPricingDto> | undefined;
  const model = brief.basePricingModel;
  if (!current && !model) {
    return undefined;
  }
  const fallback = (current?.fifteenSecPrice ?? 0) || (current?.thirtySecPrice ?? 0) || (current?.sixtySecPrice ?? 0);
  const amountFor = (key: 'fifteenSecPrice' | 'thirtySecPrice' | 'sixtySecPrice'): number => model?.[key] ?? fallback;
  return {
    fifteenSecPrice: seconds === 15 ? amountFor('fifteenSecPrice') : 0,
    thirtySecPrice: seconds === 30 ? amountFor('thirtySecPrice') : 0,
    sixtySecPrice: seconds === 60 ? amountFor('sixtySecPrice') : 0,
    facePrice: current?.facePrice ?? model?.facePrice ?? 0,
    ugcPrice: current?.ugcPrice ?? model?.ugcPrice ?? 0,
    advertisingPrice: current?.advertisingPrice ?? model?.advertisingPrice ?? 0,
    socialPostPrice: current?.socialPostPrice ?? model?.socialPostPrice ?? 0,
    commissionPercentage: current?.commissionPercentage ?? model?.commissionPercentage ?? 0,
    licenseCommissionPercentage: current?.licenseCommissionPercentage ?? model?.licenseCommissionPercentage ?? 0,
    partnerContribution: current?.partnerContribution ?? model?.partnerContribution ?? 0,
    currencyCode: current?.currencyCode ?? model?.currencyCode ?? 'GBP',
    ranks: current?.ranks ?? model?.ranks ?? [{ rank: 0, multiplier: 1 }],
    createdBy: current?.createdBy ?? '',
    influencerLevels: current?.influencerLevels ?? model?.influencerLevels ?? [],
    // Re-pricing a piece brief must keep its licence choice: with every rate stored, the API can't infer it.
    ...(brief.pieces?.length ? { adLicenseSelected: current?.adLicenseSelected ?? null } : {}),
  };
}
